<template>
  <li
    v-if="visible"
    :id="id"
    :class="containerKls"
    role="option"
    :aria-disabled="isDisabled || undefined"
    :aria-selected="itemSelected"
    @mouseenter="hoverItem"
    @click.stop="selectOptionClick"
  >
    <slot>
      <el-checkbox
        ref="checkBoxRef"
        :key="currentValue"
        :label="currentValue"
        @click.stop="selectOptionClick"
      >
        <span>{{ currentLabel }}</span>
        <span class="text-sub">{{ currentTip }}</span>
      </el-checkbox>
      <div v-if="onlyThis" class="select-tag" @click="onlyCheck">
        <span>仅筛选此项</span>
      </div>
    </slot>
  </li>
</template>

<script lang="ts">
// @ts-nocheck
import {
  computed,
  defineComponent,
  getCurrentInstance,
  nextTick,
  onBeforeUnmount,
  reactive,
  toRefs,
  unref,
} from 'vue'
import { ElCheckbox } from '@code-monkey-ui/components/checkbox'
import { useId, useNamespace } from '@code-monkey-ui/hooks'
import { useOption } from './useOption'
import type { DshSelectOptionProxy } from './token'

export default defineComponent({
  name: 'ElDshOption',
  componentName: 'ElDshOption',
  components: {
    ElCheckbox,
  },
  props: {
    /**
     * @description value of option
     */
    value: {
      required: true,
      type: [String, Number, Boolean, Object],
    },
    /**
     * @description label of option, same as `value` if omitted
     */
    label: [String, Number],
    created: Boolean,
    /**
     * @description whether option is disabled
     */
    disabled: Boolean,
    /**
     * @description additional description
     */
    tip: [String, Number],
  },

  setup(props) {
    const ns = useNamespace('dsh-select')
    const id = useId()
    const containerKls = computed(() => [
      ns.be('dropdown', 'item'),
      ns.is('disabled', unref(isDisabled)),
      {
        selected: unref(itemSelected),
        hover: unref(hover),
      },
    ])

    const states = reactive({
      index: -1,
      groupDisabled: false,
      visible: true,
      hitState: false,
      hover: false,
    })

    const {
      currentLabel,
      itemSelected,
      isDisabled,
      select,
      currentTip,
      hoverItem,
      checkBoxRef,
      currentValue,
      onlyThis,
    } = useOption(props, states)

    const { visible, hover } = toRefs(states)

    const vm = getCurrentInstance().proxy

    select.onOptionCreate(vm as unknown as DshSelectOptionProxy)

    onBeforeUnmount(() => {
      const key = (vm as unknown as DshSelectOptionProxy).value
      const { selected } = select
      const selectedOptions = select.props.multiple ? selected : [selected]
      const doesSelected = selectedOptions.some((item) => {
        return item.value === (vm as unknown as DshSelectOptionProxy).value
      })
      // if option is not selected, remove it from cache
      nextTick(() => {
        if (select.cachedOptions.get(key) === vm && !doesSelected) {
          select.cachedOptions.delete(key)
        }
      })
      select.onOptionDestroy(key, vm)
    })

    // 如果是点击选择框无需计算与调用CheckBox组件绑定的相关方法，但需要计算全选后CheckBox的勾选项
    // 如果是点击外部触发的，则需要根据选择内容计算CheckBox勾选项
    const selectOptionClick = (value) => {
      // 全选
      if (props.value === 'select-all') {
        select.handleOptionSelect('select-all')
        nextTick(() => {
          value.target.className.includes('select-dropdown') &&
            select.handleCheckAllChange(!select.checkAll)
          select.resetCheckBoxValues()
        })
        return
      }
      // 反选
      if (props.value === 'reverse') {
        select.handleOptionSelect('reverse')
        nextTick(() => {
          select.resetCheckBoxValues()
        })
        return
      }
      // 选择已被筛选的元素
      if (props.value === 'select-hidden') {
        select.handleOptionSelect('select-hidden')
        nextTick(() => {
          select.resetCheckBoxValues()
        })
        return
      }
      if (props.disabled !== true && states.groupDisabled !== true) {
        select.handleOptionSelect(vm)
        nextTick(() => {
          value.target.className.includes('select-dropdown') && [
            select.resetCheckBoxValues(),
            select.handleCheckedChange(),
          ]
        })
      }
    }

    const onlyCheck = (event) => {
      select.deleteSelected(event)
      setTimeout(() => {
        select.handleOptionSelect(vm)
        nextTick(() => {
          select.resetCheckBoxValues()
          select.handleCheckedChange()
          select.handleConfirm()
        })
      })
    }

    return {
      ns,
      id,
      containerKls,
      currentLabel,
      currentValue,
      currentTip,
      itemSelected,
      isDisabled,
      select,
      hoverItem,
      visible,
      hover,
      selectOptionClick,
      states,
      checkBoxRef,
      onlyThis,
      onlyCheck,
    }
  },
})
</script>
